import React, {useEffect} from 'react';
import {useAuth} from "../../../context";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useGetJson, usePostJson} from "../../../hooks";
import {endpoints, regex} from "../../../utils2";
import {Alert, Button, OverlayTrigger, Stack, Tooltip} from "react-bootstrap";
import {AdminUserAlert, ColumnizeText, OurAlert, PageTitle, PasswordButton, SingleText} from "../../_components";
import * as yup from "yup";
import {Formik, Form as FormikForm} from "formik";
import {WhiteCard} from "../_components/WhiteCard";
import {SoundControl} from "./SoundControl";
import {GoBackConfig} from "../_components/GoBackConfig";
import {ProgressSteps} from "../_components/ProgressSteps";
import {ABBreadcrumbs} from "../_components/ABBreadcrumbs";

export function AddEditStreamPlayback() {
    const {uid, pid} = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (pid === "new" && location.state == null) {
            navigate(`/device/${uid}`)
        }
    }, [location])

    if (location.state) {
        return <AddEditStreamPlaybackInt/>
    }
    return null
}

function AddEditStreamPlaybackInt() {
    const {authToken} = useAuth()
    const {uid, did, pid} = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const currentStep = location.state.currentStep

    const edit = pid !== "new"

    const [ev, loadingEvent, errorEvent, clearErrorEvent] = useGetJson(pid === "new" ? null : endpoints.event.get(pid), authToken?.jwt)
    const event = ev?.data
    const config2 = event?.config2

    const [submitFormAdd, _, loadingAdd, errorAdd, clearErrorAdd] = usePostJson(endpoints.event.add(did), authToken?.jwt)

    const [submitFormEdit, _2, loadingEdit, errorEdit, clearErrorEdit] = usePostJson(endpoints.event.edit(pid), authToken?.jwt)

    const validationSchema = yup.object().shape({
        main_host: yup.string().required("Radio is required")
            .test("is-valid-server-or-ip", "Invalid format. Expected a domain (e.g., http://example.com/stream) or an IP address (e.g., http://192.168.1.15/stream).", (value) => {
                return regex.url_pattern.test(value) || regex.ip_url_pattern.test(value);
            }),
        main_timeout: yup.number().typeError("Timeout must be a valid number").required("Timout is required").min(10, "Timeout must be at least 15")
            .max(120, "Timeout must be at most 120"),
        fail_host: yup.string().test("is-valid-server-or-ip", "Invalid format. Expected a domain (e.g., http://example.com/stream) or an IP address (e.g., http://192.168.1.15/stream).", (value) => {
            return !value || regex.url_pattern.test(value) || regex.ip_url_pattern.test(value);
        }),
        fail_timeout: yup.number().typeError("Timeout must be a valid number").required("Timout is required").min(10, "Timeout must be at least 15")
            .max(120, "Timeout must be at most 120"),
        delay: yup.number().required("Delay is required"),
    })

    const initialValues = {
        // Playback
        main_host: location.state?.playback?.main_host || config2?.main_host || "",
        main_timeout: location.state?.playback?.main_timeout || config2?.main_timeout || 15,
        main_username: location.state?.playback?.main_username || config2?.main_username || "",
        main_password: location.state?.playback?.main_password || config2?.main_password || "",
        fail_host: location.state?.playback?.fail_host || config2?.fail_host || "",
        fail_timeout: location.state?.playback?.fail_timeout || config2?.fail_timeout || 15,
        fail_username: location.state?.playback?.fail_username || config2?.fail_username || "",
        fail_password: location.state?.playback?.fail_password || config2?.fail_password || "",
        delay: location.state?.playback?.delay || config2?.delay || 15,
        volume: location.state?.playback?.volume || config2?.volume || 235,
        balance: location.state?.playback?.balance || (Math.round(25 / 100 * config2?.balance)) || 0,
        treble_ampl: location.state?.playback?.treble_ampl || config2?.treble_ampl || 1,
        treble_freq: location.state?.playback?.treble_freq || config2?.treble_freq || 1,
        bass_ampl: location.state?.playback?.bass_ampl || config2?.bass_ampl || 4,
        bass_freq: location.state?.playback?.bass_freq || config2?.bass_freq || 3
    }

    function handleOnSubmit(values, action) {
        const playback = {...values, balance: Math.round(100 / 25 * values.balance)}
        const payload = {...location.state, name: location.state?.name || event?.name, playback: playback, currentStep: currentStep + 1}

        if (currentStep === location.state.steps.length - 1 && !edit) {
            submitFormAdd(payload, jsonTime => {
                action.setSubmitting(false)
                navigate(location.state.steps[0])
            })
        } else if (edit) {
            submitFormEdit(payload, json => {
                action.setSubmitting(false)
                navigate(`/device/${uid}/${did}/config`)
            })
        } else {
            navigate(location.state.steps[currentStep + 1], {state: {...location.state, ...payload}})
        }
    }

    if (edit && loadingEvent) {
        return <div>Loading...</div>;
    }

    const submitButton = (edit || currentStep === (location.state.steps.length - 1)) ? "Save" : "Next"

    return <Stack>
        <AdminUserAlert/>
        <Alert variant="danger" show={errorEvent} dismissible onClose={clearErrorEvent}>{errorEvent}</Alert>
        <Alert variant="danger" show={errorAdd} dismissible onClose={clearErrorAdd}>{errorAdd}</Alert>
        <Alert variant="danger" show={errorEdit} dismissible onClose={clearErrorEdit}>{errorEdit}</Alert>

        <ABBreadcrumbs breadcrumbList={[
            {name: "Your devices", shortName: "Devices", to: `/device/${uid}`},
            {name: "Configure Device", shortName: "Configure", to: `/device/${uid}/${did}/config`},
            {name: "Playback"}]}
        />

        <PageTitle title="Set Playback">{edit ? `Edit ${event?.name}` : `Add`}.</PageTitle>


        <Formik validationSchema={validationSchema} onSubmit={handleOnSubmit} initialValues={initialValues} enableReinitialize={true}>
            {({isSubmitting, resetForm, values}) => (
                <FormikForm>
                    <ProgressSteps steps={location.state.steps.slice(1)} currentStep={currentStep}
                                   state={{...location.state, name: location.state?.name || event?.name, playback: values}} edit={edit}/>

                    <PlaybackStreamForm/>

                    <SoundControl/>

                    <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                        <GoBackConfig state={{...location.state, name: location.state?.name || event?.name, playback: values}} edit={edit}/>
                        <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                        <Button type="submit" variant="primary" disabled={isSubmitting}>{submitButton}</Button>
                    </Stack>

                </FormikForm>
            )}
        </Formik>


    </Stack>

}

export function PlaybackStreamForm() {
    return <div>
        <WhiteCard>
            <div className="fs-5 fw-semibold">Stream From</div>
            <Stack direction="horizontal" className="flex-wrap mb-2" gap={2}>
                <SingleText name="main_host" label="Radio" maxLength="128" placeholder="https://" className="flex-grow-1"/>
                <OverlayTrigger placement="top" overlay={<Tooltip>Time in seconds before the connection is considered failed</Tooltip>}>
                    <SingleText name="main_timeout" label="Timeout" postfix="sec" className=""/>
                </OverlayTrigger>
            </Stack>

            <ColumnizeText>
                <SingleText name="main_username" label="Username" autoComplete="off"/>
                <PasswordButton name="main_password" label="Password" position="right" autoComplete="new-password"/>
            </ColumnizeText>
        </WhiteCard>

        <WhiteCard>
            <div className="fs-5 fw-semibold">Stream Fail Over</div>
            <Stack direction="horizontal" className="flex-wrap mb-2" gap={2}>
                <SingleText name="fail_host" label="Radio" maxLength="128" placeholder="https://" className="flex-grow-1"/>
                <OverlayTrigger placement="top" overlay={<Tooltip>Time in seconds before the connection is considered failed</Tooltip>}>
                    <SingleText name="fail_timeout" label="Timeout" postfix="sec" className=""/>
                </OverlayTrigger>
            </Stack>

            <ColumnizeText>
                <SingleText name="fail_username" label="Username"/>
                <PasswordButton name="fail_password" label="Password" position="right"/>
            </ColumnizeText>
        </WhiteCard>

        <WhiteCard>
            <div className="fs-5 fw-semibold">Sync Delay</div>
            <SingleText name="delay" label="Delay" postfix="msec" className=""/>
        </WhiteCard>

    </div>
}